import { useEffect, useState } from "react";
import {
  Share2,
  Copy,
  Check,
  Loader2,
  Eye,
  X,
  Twitter,
  Linkedin,
  Facebook,
  Globe,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  disablePublicShare,
  enablePublicShare,
  getPublicShareStatus,
} from "../lib/api";
import { track } from "../lib/analytics";

/**
 * ShareLessonDialog — Public share link dialog (Feb 2026).
 *
 * Generates a server-rendered public URL teachers can post to social
 * media. Distinct from Community sharing (which is gated to other
 * paid Atomic Edu users). The public URL hits the backend at
 * `/api/p/{token}` and renders an OG-tagged HTML page with a branded
 * CTA banner — perfect for X / LinkedIn / Facebook link previews.
 */
const xIntent = (url, title) =>
  `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(
    title,
  )}&hashtags=AtomicEdu,OntarioEdu`;

const linkedInIntent = (url) =>
  `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;

const facebookIntent = (url) =>
  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;

const buildPostText = (activityName) =>
  `I just made "${activityName}" with Atomic Edu — Ontario Grades 4–8 lesson planning in 60 seconds.`;

export const ShareLessonDialog = ({
  open,
  onOpenChange,
  activityId,
  activityName,
  testid = "share-lesson",
}) => {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [viewCount, setViewCount] = useState(0);
  const [copied, setCopied] = useState(false);

  // Pull current status whenever the dialog opens.
  useEffect(() => {
    if (!open || !activityId) return;
    let alive = true;
    const run = async () => {
      setLoading(true);
      try {
        const res = await getPublicShareStatus(activityId);
        if (!alive) return;
        setEnabled(!!res.enabled);
        setShareUrl(res.share_url || "");
        setViewCount(res.view_count || 0);
      } catch {
        if (!alive) return;
        setEnabled(false);
        setShareUrl("");
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [open, activityId]);

  const handleEnable = async () => {
    setBusy(true);
    try {
      const res = await enablePublicShare(activityId);
      setEnabled(true);
      setShareUrl(res.share_url);
      setViewCount(res.view_count || 0);
      track("public_share_enabled", { activity_id: activityId });
      toast.success("Public share link is live");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't enable the share link.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await disablePublicShare(activityId);
      setEnabled(false);
      setShareUrl("");
      track("public_share_disabled", { activity_id: activityId });
      toast.success("Public link revoked");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't revoke the link.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      track("public_share_copied", { activity_id: activityId });
      toast.success("Link copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Select the URL manually.");
    }
  };

  const fireSocialClick = (channel) => {
    track("public_share_social_clicked", {
      activity_id: activityId,
      channel,
    });
  };

  const postText = buildPostText(activityName || "this lesson");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid={`${testid}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1c2d3d]">
            <Share2 className="h-5 w-5 text-[#1c2d3d]" />
            Share publicly
          </DialogTitle>
          <DialogDescription className="sr-only">
            Create a public, read-only link to this lesson that anyone can open
            without signing in.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          <p className="text-sm text-[#4a6984] leading-relaxed">
            Create a read-only link anyone can open — no sign-in required.
            Perfect for X, LinkedIn, Facebook, or sending to a colleague.
            Every link includes an &ldquo;Atomic Edu&rdquo; banner inviting visitors
            to create their own lesson free.
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[#4a6984]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking status…
            </div>
          ) : !enabled ? (
            <button
              type="button"
              onClick={handleEnable}
              disabled={busy}
              data-testid={`${testid}-enable-btn`}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1c2d3d] text-white font-bold py-3 px-5 rounded-full hover:bg-[#0f1e2e] transition disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Globe className="h-4 w-4" />
              )}
              Create public link
            </button>
          ) : (
            <>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#4a6984]/70">
                  Your public link
                </label>
                <div className="flex items-stretch gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    data-testid={`${testid}-url-input`}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 px-3 py-2 text-sm font-mono border border-[#4a6984]/20 rounded-lg bg-[#f4f5f7] text-[#1c2d3d] truncate"
                  />
                  <button
                    type="button"
                    onClick={handleCopy}
                    data-testid={`${testid}-copy-btn`}
                    className="inline-flex items-center gap-1.5 bg-[#96ded1] text-[#1c2d3d] font-bold px-3 rounded-lg hover:bg-[#7fd0c1] transition"
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                {viewCount > 0 && (
                  <p className="text-xs text-[#4a6984] flex items-center gap-1.5">
                    <Eye className="h-3.5 w-3.5" />
                    {viewCount} view{viewCount === 1 ? "" : "s"} so far
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#4a6984]/70">
                  Post to
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <a
                    href={xIntent(shareUrl, postText)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => fireSocialClick("x")}
                    data-testid={`${testid}-x-btn`}
                    className="inline-flex flex-col items-center justify-center gap-1.5 py-3 rounded-lg border border-[#4a6984]/15 bg-white hover:bg-[#f4f5f7] transition text-[#1c2d3d]"
                  >
                    <Twitter className="h-5 w-5" />
                    <span className="text-xs font-bold">X / Twitter</span>
                  </a>
                  <a
                    href={linkedInIntent(shareUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => fireSocialClick("linkedin")}
                    data-testid={`${testid}-linkedin-btn`}
                    className="inline-flex flex-col items-center justify-center gap-1.5 py-3 rounded-lg border border-[#4a6984]/15 bg-white hover:bg-[#f4f5f7] transition text-[#1c2d3d]"
                  >
                    <Linkedin className="h-5 w-5" />
                    <span className="text-xs font-bold">LinkedIn</span>
                  </a>
                  <a
                    href={facebookIntent(shareUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => fireSocialClick("facebook")}
                    data-testid={`${testid}-facebook-btn`}
                    className="inline-flex flex-col items-center justify-center gap-1.5 py-3 rounded-lg border border-[#4a6984]/15 bg-white hover:bg-[#f4f5f7] transition text-[#1c2d3d]"
                  >
                    <Facebook className="h-5 w-5" />
                    <span className="text-xs font-bold">Facebook</span>
                  </a>
                </div>
              </div>

              <button
                type="button"
                onClick={handleDisable}
                disabled={busy}
                data-testid={`${testid}-revoke-btn`}
                className="w-full inline-flex items-center justify-center gap-2 text-sm text-[#4a6984] hover:text-[#1c2d3d] hover:bg-[#f4f5f7] py-2 rounded-lg transition disabled:opacity-60"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
                Revoke link
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareLessonDialog;
